import { getMaxQuantityString, getQuantityMapFromStorage } from '@digital-retail/store-manager';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { constants } from '../../../config/constants';
import { withApplicationContext } from '../../../utils/ApplicationContext';
import { withCart } from '../../../utils/CartContext';
import { noop } from '../../../utils/noop';
import { withUser } from '../../../utils/UserContext';
import { ShippingPopOver } from '../../ShippingPopOver/ShippingPopOver';
import { addToCartButtonStyle } from './AddToCartButton.style';

const AddToCartButton = ({
  addToCart,
  appCtx,
  cart,
  colorIndex,
  isAddToCartInProgress,
  product,
  setIsAddToCartInProgress,
}) => {
  const { skuId } = product;
  const { setBusy } = appCtx;
  const timeputForLoginModalOn401 = appCtx?.siteConfig?.textDictionary?.TIMEOUT_FOR_LOGIN_MODAL ?? 1000;

  const [hasReachedMaxQty, setHasReachedMaxQty] = useState(false);
  const { cartItems, removeProduct: onRemoveFromCart, limitAlerts } = cart;
  const item = cartItems?.[0]?.skuId === skuId ? cartItems[0] : {};
  const { totalQuantity = 0, commerceItemId, cartLineId = '' } = item;
  const [inputVal, setInputVal] = useState(totalQuantity);
  const [cartLineIdValue, setCartLineIdValue] = useState(cartLineId);
  const [limit, setLimit] = useState(product?.measurements?.maxUnits || 999);
  const [isShippingPopOverEnabled, setIsShippingPopOverEnabled] = useState(false);
  const [deliveryType, setDeliveryType] = useState('');
  const minSaleQty = product?.measurements?.minSaleQty || 1;
  const unit = product?.measurements?.unit || 'UN';

  useEffect(() => {
    setBusy(isAddToCartInProgress);
  }, [isAddToCartInProgress]);

  useEffect(() => {
    const quantitiesMap = getQuantityMapFromStorage();
    if (cart?.cartItems?.[0]?.skuId === skuId) {
      const skuCartItems = cart.cartItems[0];
      const skuQtyLimits = skuCartItems.qtyLimits;
      const skuTotalQty = skuCartItems.totalQuantity;
      setInputVal(skuTotalQty);
      if (skuQtyLimits) {
        setLimit(skuQtyLimits);
      }
      setHasReachedMaxQty(skuTotalQty >= limit);
    } else if (quantitiesMap.has(skuId)) {
      const {
        quantity = totalQuantity,
        cartLineId: cookieCartLineId = cartLineId,
        quantityLimit,
      } = quantitiesMap.get(skuId);
      setInputVal(quantity);
      setCartLineIdValue(cookieCartLineId);
      setLimit(quantityLimit);
      setHasReachedMaxQty(quantity >= quantityLimit);
    } else {
      setInputVal(0);
      setCartLineIdValue('');
      setHasReachedMaxQty(false);
    }
  }, [cart, skuId]);

  useEffect(() => {
    const quantitiesMap = getQuantityMapFromStorage();

    if (quantitiesMap.has(skuId)) {
      const { cartLineId: cookieCartLineId } = quantitiesMap.get(skuId) || {};
      if (cookieCartLineId) {
        setCartLineIdValue(cookieCartLineId);
      }
    }
  }, [inputVal]);

  useEffect(() => {
    if (limitAlerts && (limitAlerts[cartLineId] || limitAlerts.basket)) {
      if (limitAlerts[cartLineId]) {
        setHasReachedMaxQty(true);
      }
    }
  }, [limitAlerts]);

  const handleRemoveItem = () => {
    const changeDeliveryType = inputVal - 1 <= product.expressDeliveryLimit;
    setIsAddToCartInProgress(true);
    const option = {
      appCtx,
    };

    option.products = [
      {
        cartLineId: cartLineId || cartLineIdValue,
        totalQuantity: Number(inputVal),
        commerceItemId,
        quantity: 1,
      },
    ];

    if (changeDeliveryType) {
      option.changeDeliveryType = changeDeliveryType;
    }

    onRemoveFromCart(option)
      .then((res) => {
        setIsAddToCartInProgress(false);
        if (res) {
          const errorCode = res?.[0]?.code ?? '';
          if (errorCode === constants.ATC_SESSION_EXPIRED_CODE) {
            setTimeout(() => {
              cart.setShowCart(false);
              cart.setShowLoginForm(true);
            }, timeputForLoginModalOn401);
          }
        }
      })
      .catch(() => {
        setIsAddToCartInProgress(false);
      });
  };

  const handleAddItem = () => {
    if (!hasReachedMaxQty) {
      const newQuantity = inputVal + 1;
      const { expressDeliveryLimit } = product;

      const changeDeliveryType =
        typeof expressDeliveryLimit === 'number' && expressDeliveryLimit !== 0 && newQuantity > expressDeliveryLimit;

      addToCart(colorIndex, changeDeliveryType, cartLineIdValue, newQuantity);
    }
  };
  const roundOffPrice = (value, precision = 2) => {
    const power = 10 ** (precision || 0);
    return Math.round(value * power) / power;
  };
  const displayUnit = () => {
    return `${roundOffPrice(inputVal * minSaleQty)} ${unit}`.toLowerCase();
  };

  const preventParentEvents = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
  };

  const handleShippingPopOverClose = () => {
    setIsShippingPopOverEnabled(false);
  };

  const prevInputValRef = useRef(inputVal);

  useEffect(() => {
    const deliveryLimit = product.expressDeliveryLimit;
    const prevInputVal = prevInputValRef.current;
    const hasExceededLimit = isAddToCartInProgress && inputVal === deliveryLimit + 1 && deliveryLimit > 0;
    const hasDroppedBelowLimit = inputVal <= deliveryLimit && prevInputVal > deliveryLimit;

    if (hasExceededLimit) {
      setDeliveryType(constants.AVAILABILITY_HOMEDELIVERY);
      setIsShippingPopOverEnabled(true);
    } else if (hasDroppedBelowLimit && deliveryLimit !== 0) {
      setDeliveryType(constants.AVAILABILITY_EXPRESS);
      setIsShippingPopOverEnabled(true);
    } else {
      setIsShippingPopOverEnabled(false);
    }
    prevInputValRef.current = inputVal;
  }, [inputVal, product.expressDeliveryLimit]);

  return (
    <>
      <div
        key={`add-to-cart-button-${product.offeringId}`}
        id={`add-to-cart-button-${product.offeringId}`}
        role="button"
        onKeyDown={noop}
        className="add-to-cart"
        onClick={(e) => preventParentEvents(e)}
        tabIndex={0}
      >
        <button
          id="remove-from-cart-button"
          className="remove-from-cart-button"
          type="button"
          onClick={handleRemoveItem}
          hidden={!inputVal}
        >
          {inputVal > 1 ? <span className="remove-from-cart-minus-icon" /> : <span className="remove-from-cart-icon" />}
        </button>
        <ShippingPopOver
          isOpen={isShippingPopOverEnabled}
          deliveryType={deliveryType}
          onClose={handleShippingPopOverClose}
        >
          <div className="count-box" hidden={!inputVal}>
            {displayUnit()}
          </div>
        </ShippingPopOver>

        <button
          type="button"
          className={`add-to-cart-button ${hasReachedMaxQty ? 'disabled' : ''}`}
          onClick={handleAddItem}
        >
          <span className="add-to-cart-icon" />
        </button>
      </div>
      <div className="quantity-display">
        {hasReachedMaxQty ? (
          <div className="max-qnty">{getMaxQuantityString({ maxUnits: limit * minSaleQty, unit })}</div>
        ) : (
          <div className="empty-quantity-display-space" />
        )}
      </div>
      <style jsx>{addToCartButtonStyle}</style>
    </>
  );
};

AddToCartButton.defaultProps = {
  cart: {},
};

AddToCartButton.propTypes = {
  product: PropTypes.object.isRequired,
  addToCart: PropTypes.func.isRequired,
  colorIndex: PropTypes.func.isRequired,
  appCtx: PropTypes.object.isRequired,
  cart: PropTypes.object,
  isAddToCartInProgress: PropTypes.bool.isRequired,
  setIsAddToCartInProgress: PropTypes.bool.isRequired,
};

export { AddToCartButton };
export default withApplicationContext(withCart(withUser(AddToCartButton)));
